import { createElement } from '../../core/dom/helpers.js';

/** @typedef {'light'|'dark'} ThemeValue */

// Sun (\u2600) and moon (\u263D) are emoji-variant codepoints that iOS/WebKit
// auto-promotes to full-colour emoji, so the button looked different on iPhone
// than on desktop. The trailing \uFE0E (VS15, text-presentation selector) forces
// the monochrome text glyph everywhere, matching the rest of the chrome.
/** @type {Record<ThemeValue, string>} */
const ICONS = { light: '\u2600\uFE0E', dark: '\u263D\uFE0E' };
/** @type {Record<ThemeValue, string>} */
const LABELS = { light: 'Theme: light', dark: 'Theme: dark' };
/** @type {Record<ThemeValue, ThemeValue>} */
const CYCLE = { light: 'dark', dark: 'light' };

/**
 * Theme toggle button. Two-state cycle: dark ↔ light.
 *
 * Click-only handler — <button> elements synthesize click from Enter/Space
 * natively, so no separate keydown handler is needed.
 *
 * @param {HTMLElement} container
 * @param {{ theme: 'light'|'dark', onChange: (next: 'light'|'dark') => void }} opts
 * @returns {{ update(theme: 'light'|'dark'): void, destroy(): void }}
 */
export function createThemeToggle(container, { theme, onChange }) {
  let current = theme;

  const btn = createElement('button', {
    type: 'button',
    class: 'theme-toggle',
    'aria-label': LABELS[current],
  }, [ICONS[current]]);

  btn.addEventListener('click', () => {
    const next = CYCLE[current];
    onChange(next);
  });

  container.appendChild(btn);

  /** @param {ThemeValue} t */
  function update(t) {
    current = t;
    btn.textContent = ICONS[t];
    btn.setAttribute('aria-label', LABELS[t]);
  }

  function destroy() {
    btn.remove();
  }

  return { update, destroy };
}
